// src/Components/ProductDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CreditCardCheckout from './CreditCardCheckout';
import ModelUploader from './ModelUploader';
import ModelViewer from './ModelViewer';

const ProductDetail = ({ products, isWalletConnected, onConnect, isLoggedIn, userId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreditCardCheckout, setShowCreditCardCheckout] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [showModelUploader, setShowModelUploader] = useState(false);

  useEffect(() => {
    // In a real app, you would fetch the product from your API
    // const fetchProduct = async () => {
    //   const response = await fetch(`/api/nfts/${id}`);
    //   const data = await response.json();
    //   setProduct(data);
    //   setLoading(false);
    // };
    
    // For this example, we'll use the products prop
    const foundProduct = products.find(p => p.id === parseInt(id));
    if (foundProduct) {
      setProduct(foundProduct);
    } else {
      // Product not found, redirect to home
      navigate('/');
    }
    setLoading(false);
  }, [id, products, navigate]);

  // Determine if the current user is the product owner
  const isProductOwner = isLoggedIn && product && product.creator && 
    (userId === product.creator.id || userId === product.creator_id);

  const handlePurchase = async () => {
    if (!isWalletConnected) {
      // If wallet is not connected, prompt for connection
      onConnect();
      return;
    }
    
    // In a real application, connect to your smart contract
    // const transaction = await contract.methods.purchaseNFT(id).send({
    //   from: walletAddress,
    //   value: ethers.utils.parseEther(price)
    // });
    
    alert(`Purchase initiated for ${product.title} at ${product.price} ETH`);
  };

  const handleCreditCardPurchase = () => {
    if (!isLoggedIn) {
      // Redirect to login page if not logged in
      navigate('/login', { state: { redirect: `/nft/${id}` } });
      return;
    }
    
    setShowCreditCardCheckout(true);
  };

  const handleCreditCardSuccess = (txId) => {
    setTransactionId(txId);
    setPurchaseSuccess(true);
    setShowCreditCardCheckout(false);
    
    // In a real application, you might want to refresh the user's collection
    // or update some UI state to reflect the purchase
  };

  const handleViewCollection = () => {
    navigate('/collection');
  };

  const handleUploadComplete = (uploadData) => {
    // Refresh the product data to show the new 3D model
    console.log('Upload completed:', uploadData);
    
    // In a real app, you would re-fetch the product data
    // For now, just update the UI to show upload success
    setShowModelUploader(false);
    
    // You might want to update the product state with the new model information
    // This is a simplified example - in a real app, you'd re-fetch from the server
    if (product) {
      setProduct({
        ...product,
        model: {
          type: uploadData.modelFile.split('.').pop().toLowerCase(),
          path: `/assets/3d-models/${uploadData.modelId}/object/${uploadData.modelFile}`,
          scale: 0.01,
          position: [0, 0, 0],
          rotation: [0, 0, 0]
        }
      });
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!product) {
    return <div className="error">Product not found</div>;
  }

  if (purchaseSuccess) {
    return (
      <div className="purchase-success">
        <div className="success-icon">✓</div>
        <h2>Purchase Successful!</h2>
        <p>Congratulations! You are now the owner of <strong>{product.title}</strong>.</p>
        <p>Transaction ID: <span className="transaction-id">{transactionId}</span></p>
        <p>The NFT has been added to your collection and is stored in your secure wallet.</p>
        <div className="success-actions">
          <button className="view-collection-button" onClick={handleViewCollection}>
            View My Collection
          </button>
          <button className="continue-shopping-button" onClick={() => navigate('/')}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-container">
      {showCreditCardCheckout ? (
        <CreditCardCheckout 
          product={product} 
          onSuccess={handleCreditCardSuccess} 
          onCancel={() => setShowCreditCardCheckout(false)} 
        />
      ) : showModelUploader ? (
        <div className="model-uploader-container">
          <h2>Upload 3D Model for {product.title}</h2>
          <ModelUploader
            productId={product.id}
            onUploadComplete={handleUploadComplete}
          />
          <button 
            className="cancel-button"
            onClick={() => setShowModelUploader(false)}
          >
            Cancel Upload
          </button>
        </div>
      ) : (
        <>
          <div className="product-detail-image">
            <img src={product.image} alt={product.title} />
          </div>

           {/* Add 3D model viewer if model exists */}
           {/* Temporarily disabled ModelViewer
          {product.model && (
            <div className="model-viewer-wrapper">
            <ModelViewer 
            modelPath={product.model.path}
            modelType={product.model.type}
            />
            </div>
             )}
            */}
          
          <div className="product-detail-info">
            <h1 className="product-detail-title">{product.title}</h1>
            
            <div className="product-detail-creator">
              <img src={product.creator.avatar} alt={product.creator.name} />
              <div>
                <p>Created by</p>
                <h3>{product.creator.name}</h3>
              </div>
            </div>
            
            <p className="product-detail-description">{product.description}</p>
            
            <div className="product-detail-stats">
              <div className="stat-item">
                <div className="stat-value">{product.likes}</div>
                <div className="stat-label">Likes</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{product.views}</div>
                <div className="stat-label">Views</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{product.created}</div>
                <div className="stat-label">Created</div>
              </div>
            </div>
            
            {/* 3D Model Status */}
            {product.model ? (
              <div className="model-status model-exists">
                <span className="model-badge">3D</span>
                <span className="model-status-text">This NFT includes a 3D model</span>
                {isProductOwner && (
                  <button 
                    className="update-model-button"
                    onClick={() => setShowModelUploader(true)}
                  >
                    Update 3D Model
                  </button>
                )}
              </div>
            ) : isProductOwner && (
              <div className="model-status no-model">
                <span className="model-status-text">This NFT does not have a 3D model yet</span>
                <button 
                  className="add-model-button"
                  onClick={() => setShowModelUploader(true)}
                >
                  Add 3D Model
                </button>
              </div>
            )}
            
            <div className="purchase-options">
              <div className="product-price-large">{product.price} ETH</div>
              <div className="purchase-buttons">
              {/* Temporarily disabled credit card button
                <button 
                  className="credit-card-button"
                  onClick={handleCreditCardPurchase}
                >
                  Buy with Credit Card
                </button>
                */}
                <button 
                  className="buy-now-button"
                  onClick={handlePurchase}
                >
                  {isWalletConnected ? 'Buy with Crypto' : 'Connect Wallet'}
                </button>
              </div>
              <div className="payment-info">
                <p>Credit card purchase includes:</p>
                <ul>
                  <li>No crypto wallet needed</li>
                  <li>Secure ownership with your account</li>
                  <li>Automatic conversion to blockchain asset</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductDetail;